import * as yup from "yup";
import { useMemo } from "react";
import { Row, Col } from "reactstrap";
import { FormikProps } from "formik";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../../assets/utils/lib";
import {
    StringHelper,
    ValidationHelper,
} from "../../../../assets/utils/helpers";
import { Constants } from "../../../../assets/utils/lib/constants";
import { LookupDto } from "../../../../assets/utils/lib/lookupDto";
import Drawer from "../../../../assets/vfx/drawer/drawer";
import Form from "../../../../assets/vfx/form/form";
import { useCommunity } from "../../../../context/communityContext";
import { OrganizationDto } from "../../../../services/dto/nonprofit/organizationDto";
import { ViewProps } from "./props";

interface OrganizationFormProps {
    data?: OrganizationDto;
    loading?: boolean;
    show: boolean;
    onHide: () => void;
    onSave: (values: OrganizationDto) => DrawerFormSaveResult;
    lookups?: ViewProps.OrganizationLookupProps | undefined;
    communityId?: string;
    disableCommunity: boolean;
}
const validationSchema = yup.object().shape({
    communityId: yup.string().required("Community is required."),
    name: yup.string().required("Name is required."),
    summary: yup.string().required("Summary is required."),
    webUrl: yup
        .string()
        .trim()
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .notRequired()
        .transform((v) =>
            v && !/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(v) ? `https://${v}` : v
        )
        .url("Invalid URL."),
    contactName: yup.string().required("Contact name is required."),
    contactEmail: yup
        .string()
        .required("Contact email is required.")
        .email("Invalid email."),
    contactPhone: ValidationHelper.yupValidatePhone().required(
        "Contact phone is required."
    ),
    addressLine1: yup.string().required("Address line1 is required."),
    city: yup.string().required("City is required."),
    stateId: yup.string().required("State is required."),
    zip: yup.string().required("Zip is required."),
    activationDate: yup
        .date()
        .typeError("Please enter a valid date.")
        .nullable()
        .required("Activation date is required."),
});

export const OrganizationForm = (props: OrganizationFormProps) => {
    let resourceOrganization: OrganizationDto | undefined = props.data;
    const { communityId } = useCommunity();
    const resolvedCommunityId =
        props.communityId || props.data?.communityId || communityId;
    const header: string =
        StringHelper.whenNullOrEmpty(
            resourceOrganization?.id,
            Constants.emptyGuid
        ) === Constants.emptyGuid
            ? "New Organization"
            : "Edit Organization";

    const onSubmit = (data: OrganizationDto) => {
        const updatedData: OrganizationDto = { ...data };
        return Promise.resolve(props.onSave(updatedData)).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };
    return (
        <Drawer.Form
            className="w-40"
            show={props.show}
            backdrop={true}
            placement="end"
            scroll={true}
            onHide={props.onHide}
            title={header}
            initialValues={
                resourceOrganization ?? {
                    communityId: resolvedCommunityId,
                }
            }
            validationSchema={validationSchema}
            on_Submit={onSubmit}
            enableReinitialize
        >
            {(formikProps: FormikProps<OrganizationDto>) => {
                const stateId = formikProps.values?.stateId;

                const countyOptions = useMemo(() => {
                    const all = props.lookups?.county ?? [];
                    const filtered = all.filter((x: { parentId?: string | null }) => String(x.parentId ?? "") === String(stateId ?? ""));
                    return LookupDto.mapToOptions(filtered);
                }, [props.lookups?.county, stateId]);

                const handleStateChange = (val: string | undefined) => {
                    formikProps.setFieldValue("stateId", val);
                    formikProps.setFieldValue("countyId", Constants.emptyGuid);
                };

                return (
                    <>
                        <Form.PanelSection iconClassName="bi bi-info-circle" title="1. Basic Information">
                            <Form.Text
                                name="name"
                                placeholder="Name"
                                label="Name"
                                isLoading={props.loading}
                            />
                            <Form.TextArea
                                name="summary"
                                rows={3}
                                label="Summary"
                                isLoading={props.loading}
                            />
                            {/*{!props.communityId && (*/}
                            {/*  <Form.Autocomplete*/}
                            {/*    name="communityId"*/}
                            {/*    label="Communities"*/}
                            {/*    id="communityId"*/}
                            {/*    labelKey="name"*/}
                            {/*    placeholder="Select Community"*/}
                            {/*    disabled={props.disableCommunity}*/}
                            {/*    options={*/}
                            {/*      props.lookups?.communities?.map((opt) => ({*/}
                            {/*        id: opt.key,*/}
                            {/*        name: opt.display,*/}
                            {/*      })) ?? []*/}
                            {/*    }*/}
                            {/*  />*/}
                            {/*)}*/}

                            <Form.Autocomplete
                                name="categories"
                                label="Category"
                                id="sections"
                                labelKey="name"
                                placeholder="Select Categories"
                                multiple={true}
                                options={
                                    props.lookups?.category?.map((opt) => ({
                                        id: opt.key,
                                        name: opt.display,
                                    })) ?? []
                                }
                            />
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-geo-alt"
                            title="2. Contact Information"
                            style={{ marginTop: 16 }}
                        >
                            <Form.Group label="Address" required>
                                <Row className="gx-4 gy-2">
                                    <Col xs={12}>
                                        <Form.Text name="addressLine1" placeholder="Address line 1" isLoading={props.loading} />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Text name="addressLine2" placeholder="Address line 2" isLoading={props.loading} />
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Text name="city" placeholder="City" isLoading={props.loading} />
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Dropdown
                                            name="stateId"
                                            blankOption={{
                                                key: Constants.emptyGuid,
                                                display: "--Select state--",
                                                value: Constants.emptyGuid,
                                            }}
                                            options={LookupDto.mapToOptions(props.lookups?.state, undefined, { labelKey: "code" })}
                                            onChange={(e: any) => handleStateChange(e?.target?.value)}
                                            isLoading={props.loading}
                                        />
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Zip name="zip" placeholder="Zip" isLoading={props.loading} />
                                    </Col>
                                </Row>
                            </Form.Group>

                            <Row className="gx-4 gy-3">
                                <Col xs={12}>
                                    <Form.Group label="County">
                                        <Form.Dropdown
                                            name="countyId"
                                            disabled={formikProps.values.stateId === Constants.emptyGuid || formikProps.values.stateId === undefined}
                                            isLoading={props.loading}
                                            blankOption={{
                                                key: Constants.emptyGuid,
                                                display: "--Select county--",
                                                value: Constants.emptyGuid,
                                            }}
                                            options={countyOptions}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="gx-4 gy-2">
                                <Col xs={12}>
                                    <Form.Phone name="publishedPhone" placeholder="Phone" label="Published phone" isLoading={props.loading} />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text name="publishedEmail" placeholder="Email" label="Published email" isLoading={props.loading} />
                                </Col>
                            </Row>
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-person-vcard"
                            title="3. Contact Person"
                            style={{ marginTop: 16 }}
                        >
                            <Row className="gx-4 gy-3">
                                <Col xs={12}>
                                    <Form.Text name="contactName" placeholder="Name" label="Name" isLoading={props.loading} required />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text name="contactJobTitle" placeholder="Job title" label="Job title" isLoading={props.loading} />
                                </Col>
                                <Col xs={12}>
                                    <Form.Phone name="contactPhone" placeholder="Phone" label="Phone" isLoading={props.loading} required />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text name="contactEmail" placeholder="Email" label="Email" isLoading={props.loading} required />
                                </Col>
                            </Row>
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-card-list"
                            title="3. Details"
                            style={{ marginTop: 16 }}
                        >
                            <Form.Group
                                label="Logo"
                                note="Recommended Size 125 x 125"
                                isLoading={props.loading}
                            >
                                <Form.ImageCrop
                                    name="logo"
                                    loading={props.loading}
                                    aspect={1}
                                    maxOutputWidth={125}
                                    previewMaxHeight={125}
                                    modalTitle="Logo Crop"
                                    chooseText="Choose Logo"
                                    changeText="Change Logo"
                                />
                            </Form.Group>
                            <Form.Text
                                name="webUrl"
                                placeholder="Web url"
                                label="Web url"
                                isLoading={props.loading}
                            />

                            <Form.Date
                                name="activationDate"
                                label="Activation Date"
                                placeholder="Enter date"
                            />
                        </Form.PanelSection>
                    </>
                );
            }}
        </Drawer.Form>
    );
};